import React, { useEffect, useMemo } from 'react'
import { ContainerFlexCol } from "../styles"
import { EnumFormaPagamento } from "services"
import MethodSelectorOption from "./MethodSelector"
import FormPayment from "./FormPayment"
import { Alert } from "antd"
import usePayment, { CardBase } from "hooks/usePayment"
import { formatCurrency, sum } from "utils"
import { BsExclamationTriangle } from "react-icons/bs"
import { CheckCircle } from "lucide-react"

enum EnumStatusValue {
  CORRECT = "correct",
  EXCESS = "excess",
  INSUFFICIENT = "insufficient",
}

const Form2MethodsPayment: React.FC<{ amount: number }> = ({ amount }) => {
  const { setEnableCheckout, paymentMethods } = usePayment()

  const [methodsSelecteds, setMethodsSelecteds] = React.useState([EnumFormaPagamento.CREDITO, EnumFormaPagamento.PIX])
  const [validForms, setValidForms] = React.useState([false, false])
  const [totalValue, setTotalValue] = React.useState<number[]>([])

  const isValueValid = React.useMemo(() => {
    return sum(totalValue) === amount
  }, [totalValue, amount])

  const isValidForm = React.useMemo(() => {
    return validForms.every((valid) => valid)
  }, [validForms])

  const handleChangeSelectedMethods = (method: EnumFormaPagamento, index: number) => {
    setMethodsSelecteds((prevMethods) => {
      const newMethods = [...prevMethods];
      newMethods[index] = method;
      return newMethods;
    });
  }

  function handleTotalValue(value: number, index: number) {
    setTotalValue((prevTotalValue) => {
      const newTotalValue = [...prevTotalValue];
      newTotalValue[index] = value;
      return newTotalValue;
    });
  }

  const handleValidForm = (isValid: boolean, index: number) => {
    setValidForms((prevValidForms) => {
      const newValidForms = [...prevValidForms];
      newValidForms[index] = isValid;
      return newValidForms;
    });
  }

  const missingValueValidation = useMemo(() => {
    return +(amount - totalValue.reduce((acc, curr) => acc + curr, 0)).toFixed(2)
  }, [totalValue, amount])

  useEffect(() => {
    setEnableCheckout(isValidForm && isValueValid)
  }, [isValidForm, isValueValid, totalValue])

  const getValidationStatus = () => {
    if (missingValueValidation === 0) return EnumStatusValue.CORRECT
    if (missingValueValidation < 0) return EnumStatusValue.EXCESS
    if (missingValueValidation <= amount) return EnumStatusValue.INSUFFICIENT
  }

  const validationStatus = getValidationStatus()

  const getValidationConfig = () => {
    switch (validationStatus) {
      case EnumStatusValue.INSUFFICIENT:
        return {
          type: "error" as const,
          icon: <BsExclamationTriangle />,
          message: "Valor insuficiente",
          description: `Faltam: ${formatCurrency(missingValueValidation)}`,
        }
      case EnumStatusValue.EXCESS:
        return {
          type: "warning" as const,
          icon: <BsExclamationTriangle />,
          message: "Valor excedente",
          description: `Excesso: ${formatCurrency(missingValueValidation)}`,
        }
      default:
        return {
          type: "success" as const,
          icon: <CheckCircle />,
          message: "Valor correto",
          description: "Pronto para prosseguir",
        }
    }
  }

  const validationConfig = getValidationConfig()

  const errorFirstForm = useMemo(() => (paymentMethods[0] as CardBase)?.threeDS?.error, [paymentMethods])
  const errorSecondForm = useMemo(() => (paymentMethods[1] as CardBase)?.threeDS?.error, [paymentMethods])

  useEffect(() => {
    const hasError = paymentMethods.some((method) => (method as CardBase)?.threeDS?.error)
    if (hasError) {
      setEnableCheckout(false)
    }
  }, [paymentMethods])

  return (
    <ContainerFlexCol>
      <div style={{ position: "relative", width: "100%", border: errorFirstForm ? "1px solid oklch(80.8% 0.114 19.571)" : "", borderRadius: "0 0 10px 10px" }}>
        <div style={{ position: "absolute", top: 20, right: 20, zIndex: 1 }}>
          <MethodSelectorOption methodSelected={methodsSelecteds[0]} onChange={(method) => handleChangeSelectedMethods(method, 0)} />
        </div>
        <FormPayment method={methodsSelecteds[0]} onValid={(isValid) => handleValidForm(isValid, 0)} is2MethodsChildren index={0} setCurrentValue={handleTotalValue} />
      </div>
      <div style={{ position: "relative", width: "100%", border: errorSecondForm ? "1px solid oklch(80.8% 0.114 19.571)" : "", borderRadius: "0 0 10px 10px" }}>
        <div style={{ position: "absolute", top: 20, right: 20, zIndex: 1 }}>
          <MethodSelectorOption methodSelected={methodsSelecteds[1]} onChange={(method) => handleChangeSelectedMethods(method, 1)} />
        </div>
        <FormPayment method={methodsSelecteds[1]} onValid={(isValid) => handleValidForm(isValid, 1)} is2MethodsChildren index={1} setCurrentValue={handleTotalValue} />
      </div>
      <Alert description="As taxas serão calculadas após a seleção." message="Informação" type="info" style={{ width: "100%" }} showIcon />
      {validationConfig && (
        <Alert
          style={{ width: "100%" }}
          message={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{validationConfig.message}</span>
              <span style={{ fontSize: 12 }}>
                Necessário: {formatCurrency(amount)}
              </span>
            </div>
          }
          description={validationConfig.description}
          type={validationConfig.type}
          icon={validationConfig.icon}
          showIcon
        />
      )}
    </ContainerFlexCol>
  )
}

export default Form2MethodsPayment